import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Chip, Theme, Typography } from '@mui/material';
import { CalculationStatus, ProjectStatus } from 'types/domainTypes';

function getColorTheme(value?: string): ((theme: Theme) => string) | undefined {
  switch (value) {
    case ProjectStatus.Completed:
    case CalculationStatus.Pass:
    case CalculationStatus.Completed:
      return (theme: Theme) => theme.palette.success.light;

    case CalculationStatus.Fail:
      return (theme: Theme) => theme.palette.error.light;

    default:
      return undefined;
  }
}

function GetStatusText(value: string, isCalculationStatus = false): string {
  const { t } = useTranslation();

  if (!isCalculationStatus && value === ProjectStatus.Completed) {
    return t('projects-page.project-status-completed');
  }

  switch (value) {
    case ProjectStatus.InProgress:
      return isCalculationStatus ? t('calculation.status-in-progress') : t('projects-page.project-status-in-progress');

    case CalculationStatus.Pass:
      return t('calculation.status-pass');

    case CalculationStatus.Fail:
      return t('calculation.status-fail');

    case CalculationStatus.Calculating:
      return t('calculation.status-calculating');

    case CalculationStatus.FailedToCalculate:
      return t('calculation.status-failed-to-calculate');

    case CalculationStatus.Completed:
      return t('calculation.status-completed');

    default:
      return value;
  }
}

interface StatusLabelProps {
  size: 'small' | 'medium';
  value: string;
  isCalculationStatus?: boolean;
}

export function StatusLabel({ size, value, isCalculationStatus }: StatusLabelProps): ReactElement | null {
  const text = GetStatusText(value, isCalculationStatus);

  const color = getColorTheme(value);
  return <Chip sx={{ backgroundColor: color }} size={size} label={<Typography variant="caption">{text}</Typography>} />;
}

interface CustomizedLabelProps {
  size: 'small' | 'medium';
  isCustomized?: boolean;
}

export function CustomizedLabel({ size, isCustomized }: CustomizedLabelProps): ReactElement | null {
  const { t } = useTranslation();
  const text = t('common.label-customised');

  if (!isCustomized) {
    return null;
  }

  return <Chip size={size} label={<Typography variant="caption">{text}</Typography>} />;
}
